import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  articlePublished?: string;
  articleModified?: string;
  noIndex?: boolean;
  schema?: object | object[];
}

const BASE_URL = "https://thepropertool.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/logo.png`;

export function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  articlePublished,
  articleModified,
  noIndex = false,
  schema,
}: SEOHeadProps) {
  const fullTitle = title.includes("The Proper Tool")
    ? title
    : `${title} | The Proper Tool`;

  const canonicalUrl = canonical
    ? `${BASE_URL}${canonical}`
    : BASE_URL;

  const schemaArray = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="The Proper Tool" />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ogType === "article" && articlePublished && (
        <meta property="article:published_time" content={articlePublished} />
      )}
      {ogType === "article" && articleModified && (
        <meta property="article:modified_time" content={articleModified} />
      )}

      {schemaArray.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * SOFTWARE_SCHEMA — SoftwareApplication structured data.
 *
 * Google's Rich Results spec lists three required properties for
 * SoftwareApplication rich results:
 *   1. name          ✅
 *   2. offers.price  ✅
 *   3. aggregateRating OR review  ❌  (must be real, collected data)
 *
 * Without a genuine aggregateRating or review, Google's Rich Results Test
 * returns an error and the schema is ineligible for the Software App rich
 * result card. Adding fabricated ratings would violate Google's structured
 * data quality guidelines and risk a manual action.
 *
 * DO NOT include this schema on any page until real customer review data
 * (e.g. from Trustpilot or Google Reviews) is available to populate
 * aggregateRating. When that data exists, add:
 *
 *   "aggregateRating": {
 *     "@type": "AggregateRating",
 *     "ratingValue": <real average>,
 *     "ratingCount": <real count>,
 *     "bestRating": 5,
 *     "worstRating": 1
 *   }
 *
 * and include the schema on the homepage.
 */
export const SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "The Proper Tool",
  "description": "Builder quoting software for UK tradespeople. Generate accurate, professional construction quotes in minutes using drawings, photos or plain text.",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": [
    {
      "@type": "Offer",
      "name": "Lite",
      "price": "29",
      "priceCurrency": "GBP",
      "description": "Perfect for sole traders getting started. Includes 10 AI auto-quotes per month, unlimited template and manual quotes, professional PDF export, and the full 31-category cost library.",
      "url": "https://thepropertool.com/#pricing",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "0",
        "priceCurrency": "GBP",
        "description": "14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes",
        "billingDuration": {
          "@type": "QuantitativeValue",
          "value": 14,
          "unitCode": "DAY"
        }
      }
    },
    {
      "@type": "Offer",
      "name": "Starter",
      "price": "59",
      "priceCurrency": "GBP",
      "description": "For growing businesses wanting more power. Includes 25 AI auto-quotes per month, unlimited template and manual quotes, Pricing Intelligence, expert validation pipeline, and regional UK pricing.",
      "url": "https://thepropertool.com/#pricing",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "0",
        "priceCurrency": "GBP",
        "description": "14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes",
        "billingDuration": {
          "@type": "QuantitativeValue",
          "value": 14,
          "unitCode": "DAY"
        }
      }
    },
    {
      "@type": "Offer",
      "name": "Pro",
      "price": "99",
      "priceCurrency": "GBP",
      "description": "For established businesses with high quote volume. Includes 50 AI auto-quotes per month, unlimited template and manual quotes, drawing analysis, learned rate adjustments, and all Starter features.",
      "url": "https://thepropertool.com/#pricing",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "0",
        "priceCurrency": "GBP",
        "description": "14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes",
        "billingDuration": {
          "@type": "QuantitativeValue",
          "value": 14,
          "unitCode": "DAY"
        }
      }
    }
  ],
  "url": "https://thepropertool.com",
  "screenshot": "https://thepropertool.com/logo.png",
  "featureList": [
    "Automatic quote generation from drawings and photos",
    "31-category cost library with UK market rates",
    "PDF quote export",
    "Client management",
    "Stripe payment integration"
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "UK Builders and Construction Contractors"
  },
  "provider": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  }
};

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "The Proper Tool",
  "url": "https://thepropertool.com",
  "logo": "https://thepropertool.com/logo.png",
  "description": "Builder quoting software for UK tradespeople",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "email": "hello@thepropertool.com"
  }
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "The Proper Tool",
  "url": "https://thepropertool.com",
  "description": "Builder quoting software for UK tradespeople. Generate accurate, professional construction quotes in minutes using drawings, photos or plain text."
};
